import type { FieldProp } from 'react-typed-form';

import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { color } from 'src/constants/color.css';
import { formatTimeSpent, parseTimeSpent } from 'src/util';

import { ErrorList, Stack, Text } from '../components';

type Props = Readonly<{
  field: FieldProp<number | null>;
  label?: string;
  hint?: string;
  autoFocus?: boolean;
}>;

/**
 * A duration typed the way people say it — "90m", "1h 30m" — held as the
 * minutes the API wants.  What was typed is kept while the field has focus, so
 * a half-finished "1h" is not reformatted out from under the cursor.
 */
export default function FieldTimeSpent({
  field,
  label = 'Duration',
  hint,
  autoFocus,
}: Props) {
  const [typed, setTyped] = React.useState<string | null>(null);
  const isDisabled = field.isLoading;

  return (
    <Stack gap={0.5}>
      <Text
        typeface="body.small.regular"
        color={isDisabled ? 'neutral60' : 'neutral80'}
      >
        {label}
      </Text>
      <html.input
        style={[styles.input, isDisabled && styles.inputDisabled]}
        value={typed ?? formatTimeSpent(field.value) ?? ''}
        placeholder="e.g. 1h 20m"
        autoFocus={autoFocus}
        disabled={isDisabled}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const text = e.target.value;
          setTyped(text);
          field.handleValueChange(
            text.trim() === '' ? null : parseTimeSpent(text)
          );
        }}
        onBlur={() => setTyped(null)}
      />
      {hint && (
        <Text typeface="caption.medium" color="neutral40">
          {hint}
        </Text>
      )}
      <ErrorList errors={field.lastErrorList} />
    </Stack>
  );
}

const styles = css.create({
  input: {
    padding: 12,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 8,
    fontSize: 16,
    fontFamily: 'Inter, sans-serif',
    lineHeight: 1.5,
    color: color.neutral100,
    backgroundColor: '#fff',
    outline: 'none',
    width: '100%',
    '::placeholder': {
      color: color.neutral40,
    },
  },
  inputDisabled: {
    backgroundColor: color.neutral15,
    color: color.neutral60,
    cursor: 'default',
  },
});
